import React, { useState } from "react";

import Input from "components/Input";
import Checkbox from "components/Input/Checkbox";
import Button from "components/Button";
import Select from "components/Input/Select";
import { default as MultiSelect } from "components/Input/MultiSelect";
import Drawer from "components/Drawer";
import Tooltip from "components/Tooltip";

import { checkValidity, getOptions } from "utils/fields";
import { updateObject } from "utils";
import { USER_PROFFESSIONS } from "utils/user/constants";

import TermsAndConditionsContent from "features/Resources/TermsAndConditions/Content";
import PrivacyPolicyContent from "features/Resources/PrivacyPolicy/Content";
import SoftwareLicenceContent from "features/Resources/SoftwareLicence/Content";
import resourcesClasses from "features/Resources/style.module.scss";

import classes from "./styles.module.scss";

import InfoIcon from "assets/images/icons/info.svg";

const provinceOpts = getOptions({
    fieldKey: "weatherRegion",
    version: "v11-11",
});

const REA_NOTICE =
    "Registered Energy Advisors must provide proof that they are licensed and in good standing with NRCan for access to ERS simulation mode. Volta Research staff will be in touch to verify your status.";

const ORG_REQUEST_TIP =
    "Organizations let you share models with your team and manage a shared upgrade library. Checking this lets to create one for your company.";

const LEGAL_DOCS = {
    terms: {
        title: "Terms & Conditions",
        subtitle: "Terms governing use of the Volta SNAP platform.",
        linkLabel: "Terms and Conditions",
        Content: TermsAndConditionsContent,
    },
    privacy: {
        title: "Privacy Policy",
        subtitle: "How we collect, store, use, and disclose your personal data.",
        linkLabel: "Privacy Policy",
        Content: PrivacyPolicyContent,
    },
    licence: {
        title: "Software Licensing Agreement",
        subtitle: "Terms governing the licensing and use of the Volta SNAP software.",
        linkLabel: "Software Licensing Agreement",
        Content: SoftwareLicenceContent,
    },
};

const isFieldValid = (field, value) => {
    return checkValidity(field.label, value ?? "", field.validation);
};

const Step2 = ({ handleUserDetails, processing }) => {
    const [valid, setValid] = useState(false);
    const [teamToCreate, toggleTeamToCreate] = useState(false);
    const [isAgreeWithTerms, toggleIsAgreeWithTerms] = useState(false);
    const [openLegalDoc, setOpenLegalDoc] = useState(null);
    const [accountDetails, setAccountDetails] = useState({
        firstName: {
            name: "firstName",
            value: "",
            type: "text",
            label: "First Name",
            placeholder: "Your First Name",
            valid: false,
            validation: {
                required: true,
                isPersonName: true,
            },
            validMessage: "",
            touched: false,
        },
        lastName: {
            name: "lastName",
            value: "",
            type: "text",
            label: "Last Name",
            placeholder: "Your Last Name",
            valid: false,
            validation: {
                required: true,
                isPersonName: true,
            },
            validMessage: "",
            touched: false,
        },
        province: {
            name: "province",
            value: "",
            type: "text",
            label: "Province",
            placeholder: "Your Province",
            valid: false,
            validation: {
                required: true,
            },
            validMessage: "",
            touched: false,
            inputType: "select",
            options: provinceOpts,
        },
        profession: {
            name: "profession",
            value: [],
            type: "select",
            label: "Profession",
            placeholder: "Your Profession",
            valid: false,
            validation: {
                required: true,
            },
            validMessage: "",
            touched: false,
            inputType: "multiSelect",
            options: USER_PROFFESSIONS,
        },
        company: {
            name: "company",
            value: "",
            type: "text",
            label: "Company",
            placeholder: "Your Company",
            valid: false,
            validation: {
                required: true,
                isCompanyName: true,
            },
            validMessage: "",
            touched: false,
        },
    });

    const isRea = accountDetails.profession.value.includes("registeredEnergyAdvisor");

    const onChangeHandler = (value, fieldName) => {
        const field = accountDetails[fieldName];
        const { isValid, message } = isFieldValid(field, value);

        const updatedControls = updateObject(accountDetails, {
            [fieldName]: updateObject(field, {
                value,
                valid: isValid,
                validMessage: message,
                touched: true,
            }),
        });

        setValid(Object.values(updatedControls).every(({ valid }) => valid));
        setAccountDetails(updatedControls);
    };

    const activeLegalDoc = openLegalDoc ? LEGAL_DOCS[openLegalDoc] : null;
    const LegalDocContent = activeLegalDoc?.Content;

    return (
        <div className={classes.step2Wrapper}>
            <div className={classes.step2Header}>
                <h2>Account details</h2>
                <span>To finalize your account, we need a few more details about you.</span>
                <span>
                    Volta SNAP is for professionals and students in the residential energy efficiency industry. It is
                    not for general consumer or homeowner use.
                </span>
            </div>
            <div className={classes.step2Form}>
                {Object.values(accountDetails).map(
                    ({
                        name,
                        type,
                        label,
                        valid: fieldValid,
                        validMessage,
                        touched,
                        placeholder,
                        value,
                        inputType,
                        options,
                    }) =>
                        inputType === "select" ? (
                            <Select
                                key={name}
                                large
                                name={name}
                                options={options}
                                label={label}
                                placeholder={placeholder}
                                hideLabel
                                invalid={!fieldValid}
                                validMessage={validMessage}
                                touched={touched}
                                className={classes.input}
                                input={{
                                    onChange: (value) => onChangeHandler(value, name),
                                    value,
                                }}
                                isNewDesign={true}
                            />
                        ) : inputType === "multiSelect" ? (
                            <MultiSelect
                                key={name}
                                large
                                name={name}
                                options={options}
                                label={label}
                                placeholder={placeholder}
                                hideLabel
                                invalid={!fieldValid}
                                validMessage={validMessage}
                                touched={touched}
                                className={classes.input}
                                input={{
                                    onChange: (value) => onChangeHandler(value, name),
                                    value,
                                }}
                                isNewDesign={true}
                            />
                        ) : (
                            <Input
                                key={name}
                                large
                                name={name}
                                type={type}
                                label={label}
                                placeholder={placeholder}
                                hideLabel
                                invalid={!fieldValid}
                                validMessage={validMessage}
                                touched={touched}
                                className={classes.input}
                                input={{
                                    onChange: (value) => onChangeHandler(value, name),
                                    value: value,
                                }}
                                isNewDesign={true}
                            />
                        )
                )}
            </div>
            <div className={classes.step2Footer}>
                {isRea && <p className={classes.reaNotice}>{REA_NOTICE}</p>}
                <Checkbox
                    className={classes.orgRequestCheckbox}
                    label={
                        <>
                            Create organization (optional)
                            <span
                                className={classes.orgInfoTip}
                                data-tip={ORG_REQUEST_TIP}
                                data-for="orgCreateTip"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onKeyDown={(event) => {
                                    event.stopPropagation();
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label="What is an organization?"
                            >
                                <img src={InfoIcon} alt="" />
                            </span>
                        </>
                    }
                    name="reqTeamToCreate"
                    input={{
                        value: teamToCreate,
                        onChange: toggleTeamToCreate,
                    }}
                />
                <Tooltip id="orgCreateTip" />
                <Checkbox
                    label={
                        <>
                            I agree to the{" "}
                            <button
                                type="button"
                                className={classes.termsLink}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setOpenLegalDoc("terms");
                                }}
                            >
                                {LEGAL_DOCS.terms.linkLabel}
                            </button>
                            {", "}
                            <button
                                type="button"
                                className={classes.termsLink}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setOpenLegalDoc("privacy");
                                }}
                            >
                                {LEGAL_DOCS.privacy.linkLabel}
                            </button>
                            {", and "}
                            <button
                                type="button"
                                className={classes.termsLink}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setOpenLegalDoc("licence");
                                }}
                            >
                                {LEGAL_DOCS.licence.linkLabel}
                            </button>
                        </>
                    }
                    name="isAgreeWithTerms"
                    input={{
                        value: isAgreeWithTerms,
                        onChange: toggleIsAgreeWithTerms,
                    }}
                />
                <Button
                    disabled={!valid || !isAgreeWithTerms || processing}
                    type="submit"
                    onClick={(event) => handleUserDetails(event, accountDetails, teamToCreate)}
                    style={{ width: "100%" }}
                >
                    {processing ? "Processing" : "Sign Up"}
                </Button>
            </div>
            <Drawer
                open={Boolean(activeLegalDoc)}
                close={() => setOpenLegalDoc(null)}
                anchor="bottom"
                title={activeLegalDoc?.title || ""}
                subtitle={activeLegalDoc?.subtitle || ""}
                classes={{
                    paperClass: classes.termsDrawerPaper,
                }}
                bodyClass={classes.termsDrawerBody}
            >
                <div className={resourcesClasses.contentWrapper}>{LegalDocContent && <LegalDocContent />}</div>
            </Drawer>
        </div>
    );
};

export default Step2;
